import { useState } from 'react';
import { useIdentityPrefill } from '../../hooks/useIdentityPrefill';
import styles from './ContactSection.module.css';

const TOPICS = ['Founding Member Access Request', 'Partnerships', 'Press', 'Sales', 'Support'];
const SUPPORT_EMAIL = 'support@repbuddy.io';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

export default function ContactSection() {
  const [form, setForm] = useState({ name: '', email: '', company: '', topic: 'Sales', message: '' });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Signed-in members get their name / email / company filled in, still editable.
  useIdentityPrefill(setForm);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: undefined }));
  }

  function validate() {
    const e = {};
    if (!form.name.trim()) e.name = 'Tell us your name.';
    if (!form.email.trim()) e.email = 'We need an email to reply.';
    else if (!EMAIL_RE.test(form.email.trim())) e.email = 'That email doesn’t look right.';
    if (!form.message.trim()) e.message = 'What can we help with?';
    return e;
  }

  async function handleSubmit(evt) {
    evt.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;

    setSending(true);
    setSubmitError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim(),
          topic: form.topic,
          message: form.message.trim(),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Something went wrong.');
      }
      setSent(true);
    } catch (err) {
      setSubmitError(
        err.message === 'Failed to fetch'
          ? `We couldn’t reach the server. Please try again, or email ${SUPPORT_EMAIL} directly.`
          : err.message
      );
    } finally {
      setSending(false);
    }
  }

  function resetForm() {
    setForm({ name: '', email: '', company: '', topic: 'Sales', message: '' });
    setErrors({});
    setSubmitError('');
    setSent(false);
  }

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        {/* Aside */}
        <aside className={styles.aside}>
          <div className={styles.asideBlock}>
            <span className={styles.asideLabel}>Prefer email?</span>
            <a className={styles.email} href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </div>
          <div className={styles.asideBlock}>
            <span className={styles.asideLabel}>Response time</span>
            <p className={styles.asideText}>We reply within one business day.</p>
          </div>
          <div className={styles.asideBlock}>
            <span className={styles.asideLabel}>Reach the right desk</span>
            <ul className={styles.topicList}>
              <li><strong>Founding Member Access Request</strong>: request access to the private beta</li>
              <li><strong>Sales</strong>: pricing, trials, a live walkthrough</li>
              <li><strong>Support</strong>: you're a customer and need a hand</li>
              <li><strong>Partnerships</strong>: integrations and referrals</li>
              <li><strong>Press</strong>: media and brand questions</li>
            </ul>
          </div>
        </aside>

        {/* Form */}
        <div className={styles.formWrap}>
          {sent ? (
            <div className={styles.success}>
              <span className={styles.successMark} aria-hidden="true">✓</span>
              <h2 className={styles.successHeading}>Thank you for connecting.</h2>
              <p className={styles.successText}>
                We got your message and will get back to you within 24 hours. If it's
                urgent, you can also reach us at{' '}
                <a className={styles.inlineLink} href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
              </p>
              <button className={styles.resetBtn} onClick={resetForm}>Send another</button>
            </div>
          ) : (
          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            <div className={styles.grid2}>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>Name</span>
                <input
                  className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                  type="text"
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                  placeholder="Jordan Rivera"
                />
                {errors.name && <span className={styles.error}>{errors.name}</span>}
              </label>

              <label className={styles.field}>
                <span className={styles.fieldLabel}>Work email</span>
                <input
                  className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                  type="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  placeholder="you@company.com"
                />
                {errors.email && <span className={styles.error}>{errors.email}</span>}
              </label>
            </div>

            <div className={styles.grid2}>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>Company <span className={styles.optional}>optional</span></span>
                <input
                  className={styles.input}
                  type="text"
                  value={form.company}
                  onChange={(e) => update('company', e.target.value)}
                  placeholder="Company, Inc."
                />
              </label>

              <label className={styles.field}>
                <span className={styles.fieldLabel}>Topic</span>
                <select
                  className={styles.select}
                  value={form.topic}
                  onChange={(e) => update('topic', e.target.value)}
                >
                  {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
            </div>

            <label className={styles.field}>
              <span className={styles.fieldLabel}>Message</span>
              <textarea
                className={`${styles.textarea} ${errors.message ? styles.inputError : ''}`}
                rows={5}
                value={form.message}
                onChange={(e) => update('message', e.target.value)}
                placeholder="Tell us what you're after."
              />
              {errors.message && <span className={styles.error}>{errors.message}</span>}
            </label>

            {submitError && <p className={styles.error} role="alert">{submitError}</p>}
            <button className={styles.submit} type="submit" disabled={sending}>
              {sending ? 'Sending…' : 'Send message'}
            </button>
            <p className={styles.formNote}>We reply within one business day.</p>
          </form>
          )}
        </div>
      </div>
    </section>
  );
}
